// Repairs mistyped site addresses that otherwise reach the indexer and die as
// "Invalid instagram user id" or a DNS failure on host 'instagram'.
// Conservative by default: a repair that could silently point at a different
// blog is not made. `cms` is the landing page's CMS, so /instagram-book can
// take a bare username.
//
// Scheme, host and Instagram handles are lowercased because they are all
// case-insensitive, and projid is derived from this string - a phone keyboard
// capitalising the first letter otherwise makes a second project for the same
// blog. Other blogs' paths are case-sensitive and are left as typed.

const INSTAGRAM_HOST = /^(www\.)?instagram\.com$/i
const INSTAGRAM_HOST_NO_TLD = /^(www\.)?instagram$/i
const HANDLE = /^[A-Za-z0-9._]{1,30}$/
const HANDLE_NO_DOT = /^[A-Za-z0-9_]{1,30}$/
const TLD_SUFFIX = /\.[A-Za-z]{2,6}$/
const MAIL_DOMAIN = /\.(gmail|googlemail|hotmail|yahoo|ymail|outlook|icloud|aol|gmx|live|msn|proton|protonmail|comcast|btinternet)\.[A-Za-z.]{2,}$/i

// A pasted-over-a-paste address keeps only the last real URL, which also drops
// leading junk: '.https://x' and 'www.foo..https://x' both become 'https://x'.
function lastPastedUrl(site) {
  const scheme = /https?:\/\//gi
  let last = -1
  let found
  while ((found = scheme.exec(site)) !== null) {
    last = found.index
  }
  return last > 0 ? site.slice(last) : site
}

function repairScheme(site) {
  return site.replace(/^(https?)[:/]+/i, '$1://')
}

function trimPunctuation(site) {
  return site.replace(/^[\s.,:;'"<]+/, '').replace(/[\s.,;'">]+$/, '').replace(/\/+$/, '')
}

function splitScheme(site) {
  const parts = site.match(/^(https?:\/\/)(.*)$/i)
  return parts ? [parts[1], parts[2]] : ['', site]
}

function isInstagramHost(host) {
  return INSTAGRAM_HOST.test(host) || INSTAGRAM_HOST_NO_TLD.test(host)
}

// instagram.comparry_foskett -> instagram.com + parry_foskett. The handle may
// not contain a dot, so instagram.company.com keeps its own hostname.
function splitMissingSlash(host) {
  const parts = host.match(/^((?:www\.)?instagram\.com)(.+)$/i)
  if (!parts) return null
  const handle = parts[2].replace(/^\/+/, '')
  return HANDLE_NO_DOT.test(handle) ? [parts[1], handle] : null
}

// A handle ending in a mail domain is always a mistake. A handle merely ending
// in a TLD is only probably one, so that is left to the Instagram landing page.
function repairHandle(handle, cms) {
  let repaired = handle.replace(/^@+/, '').replace(/\.+$/, '')
  if (MAIL_DOMAIN.test(repaired)) {
    repaired = repaired.replace(MAIL_DOMAIN, '')
  } else if (cms === 'instagram' && TLD_SUFFIX.test(repaired)) {
    repaired = repaired.replace(TLD_SUFFIX, '')
  }
  return HANDLE.test(repaired) ? repaired : handle
}

export function repairSite(input, cms) {
  const trimmed = (input || '').trim()
  if (!trimmed) return trimmed

  if (trimmed.charAt(0) === '@' && trimmed.indexOf('/') < 0) {
    const handle = trimmed.slice(1)
    return HANDLE.test(handle) ? 'instagram.com/' + handle.toLowerCase() : trimmed
  }
  if (cms === 'instagram' && trimmed.indexOf('/') < 0 && trimmed.indexOf('.') < 0 &&
      HANDLE.test(trimmed)) {
    return 'instagram.com/' + trimmed.toLowerCase()
  }

  const site = trimPunctuation(repairScheme(lastPastedUrl(trimmed)))
  const schemeAndRest = splitScheme(site)
  const scheme = schemeAndRest[0].toLowerCase()
  const rest = schemeAndRest[1]
  const slash = rest.indexOf('/')
  let host = (slash < 0 ? rest : rest.slice(0, slash)).toLowerCase()
  let path = slash < 0 ? '' : rest.slice(slash + 1)

  if (!path) {
    const split = splitMissingSlash(host)
    if (split) {
      host = split[0]
      path = split[1]
    }
  }
  if (INSTAGRAM_HOST_NO_TLD.test(host)) {
    host = host + '.com'
  }
  if (isInstagramHost(host) && path && path.indexOf('/') < 0) {
    path = repairHandle(path, cms).toLowerCase()
  }
  return scheme + host + (path ? '/' + path : '')
}



// WEBPACK FOOTER //
// ./src/siteurl.js